import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import tippy from 'tippy.js';
// import Tooltip from 'common/deps/uiv/Tooltip';

let timeout = null;

const ERROR_MESSAGES = {
  typeMismatch(el) {
    if (el.type === 'email') return 'Ingrese una dirección de correo electrónico válida';
    if (el.type === 'url') return 'Ingrese una dirección de correo electrónico válida';
    return 'Ingrese un formato válido';
  },
  badInput(el) {
    if (el.type === 'number') return 'Ingrese un número válido';
    return 'Ingrese un formato válido';
  },
  tooShort: el => `Debe contener al menos ${el.getAttribute('minlength')} caracteres`,
  tooLong: el => `Debe contener como máximo ${el.getAttribute('minlength')} caracteres`,
  rangeUnderflow: el => `Debe ser mayor o igual a ${el.getAttribute('min')}`,
  rangeOverflow: el => `Debe ser menor o igual a ${el.getAttribute('max')}`,
  stepMismatch(el) {
    if (!el.step) return 'Ingrese un número entero';
    return `Ingrese un número múltiplo de ${el.step}`;
  },
  patternMismatch: () => 'Ingrese un formato válido',
  valueMissing: () => 'Este campo es obligatorio',
};

const DEFAULT_ERROR_MESSAGE = 'Este campo es inválido';

const FIELD_ERROR_CLASS = '--danger';

@Component({
  props: {
    aliases: {
      type: Array,
      default: () => [],
    },
    tag: {
      type: String,
      default: 'input',
      description: 'set the element type of the input to be rendered, see computed property elementType',
    },
    tooltipPlacement: {
      type: String,
      default: 'bottom',
    },
    errorMessages: {
      type: Object,
      default: undefined,
    },
    value: {
      required: true,
    },
  },
})
export default class BbFieldComponent extends Vue {
  validityError = '';
  validityElement = null;
  isFocused = false;
  isValid = false;
  wasFormSubmitted = false;
  wasFocused = false;
  hasHover = false;
  customError = '';
  errorClass = FIELD_ERROR_CLASS;
  _tippy = null;

  constructor() {
    super();
    this.validate = this.validate.bind(this);
  }

  get elementType() {
    const { tag } = this.$props;
    const { type } = this.$attrs;
    if (tag === 'select') return 'select';
    if (tag === 'input' && type === 'checkbox') return 'checkbox';
    if (tag === 'input' && type === 'radio') return 'radio';
    if (tag === 'input') return 'input';
    if (tag === 'textarea') return 'textarea';
    return 'unknown';
  }

  get inputModel() {
    return this.value;
  }

  set inputModel(value) {
    this.$emit('input', value);
  }

  get errorMessage() {
    if (this.isValid) return '';
    if (this.customError) return this.customError;
    return this.getValidityErrorMessage(this.validityElement, this.validityError);
  }

  get tooltipMessage() {
    if (this.isErrorDisplayed) {
      return this.errorMessage;
    }
    return '';
  }

  get isErrorDisplayed() {
    return (this.wasFocused || this.wasFormSubmitted) && !this.isValid;
  }

  get showTooltip() {
    return (this.isFocused || this.hasHover) && this.isErrorDisplayed;
  }

  set showTooltip(value) {
    // noop
  }

  get errorMessagesMap() {
    return Object.assign({}, ERROR_MESSAGES, this.errorMessages);
  }

  @Watch('tooltipMessage')
  tooltipMessageChanged() {
    if (this.tooltipMessage && this._tippy) {
      const content = this._tippy.popper.querySelector('.tippy-content');
      content.textContent = this.tooltipMessage;
    }
  }

  @Watch('isErrorDisplayed')
  errorDisplayedChanged() {
    const method = this.isErrorDisplayed ? 'add' : 'remove';
    this.$el.classList[method](this.errorClass);
    if (this.isErrorDisplayed && !this._tippy) {
      this.$el.title = this.tooltipMessage;
      this._tippy = tippy.one(this.$el, {
        arrow: true,
        hideOnClick: 'persistent',
        placement: this.tooltipPlacement,
        theme: 'BbFieldTooltip',
        trigger: '',
      });
    } else {
      this._tippy.destroy();
      this._tippy = null;
      this.$el.title = '';
    }
  }

  @Watch('value')
  valueChanged() {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      this.validate();
    }, 1000);
  }

  @Watch('showTooltip')
  showTooltipChanged() {
    if (this.showTooltip) {
      this._tippy.show();
    } else if (this._tippy) {
      this._tippy.hide();
    }
  }

  mounted() {
    this.$el.bbFieldCtrl = this;
    this.listenFieldsFocus();
    this.listenMouseOver();
    this.validate();
  }

  getFields() {
    if (/^(INPUT|SELECT|TEXTAREA)$/.test(this.$el.tagName)) {
      return [this.$el];
    }
    return Array.from(this.$el.querySelectorAll('input,select,textarea'));
  }

  getFieldsGroupedByName() {
    const names = new Set();
    return this.getFields().filter((field) => {
      const { name } = field;
      if (!names.has(name)) {
        names.add(name);
        return true;
      }
      return false;
    });
  }

  getValidityErrorMessage(field, validityError) {
    const errorMsgFn = this.errorMessagesMap[validityError];
    if (!errorMsgFn) return DEFAULT_ERROR_MESSAGE;
    return errorMsgFn(field);
  }

  listenFieldsFocus() {
    const fields = this.getFields();
    fields.forEach((field) => {
      if (field.bbFieldFocus) return;
      field.bbFieldFocus = true;
      field.addEventListener('focus', () => {
        this.isFocused = true;
      });
      field.addEventListener('blur', () => {
        this.isFocused = false;
        if (!field.validity.valueMissing) {
          this.wasFocused = true;
        }
      });
    });
  }

  listenMouseOver() {
    this.$el.addEventListener('mouseover', () => {
      this.hasHover = true;
    });
    this.$el.addEventListener('mouseleave', () => {
      this.hasHover = false;
    });
  }

  validate() {
    this.resetErrors();
    this.getFieldsGroupedByName().forEach((field) => {
      field.setCustomValidity('');
      const isValid = field.checkValidity();
      if (!isValid) {
        this.isValid = false;
        const { validity } = field;
        // validity is not enumerable, so get validity keys from ERROR_MESSAGES
        const validityError = Object.keys(ERROR_MESSAGES).find(key => validity[key]) || 'unknown';
        if (!this.validityError) {
          this.validityError = validityError;
          this.validityElement = field;
        }
        field.setCustomValidity(this.getValidityErrorMessage(field, validityError));
      }
    }, {});
  }

  formSubmitted() {
    this.wasFormSubmitted = true;
    this.validate();
  }

  setFormErrors(errors) {
    this.resetErrors();
    if (!errors) return;
    const errorFields = this.getFieldsGroupedByName().filter(f => f.name in errors);
    if (errorFields.length > 0) {
      this.isValid = false;
      const firstField = errorFields[0];
      [this.customError] = errors[firstField.name];
      this.validityElement = firstField;
      errorFields.forEach((field) => {
        field.setCustomValidity(errors[field.name][0]);
      });
    }
  }

  focus() {
    const element = this.validityElement || this.getFieldsGroupedByName()[0] || this.$el;
    element.focus();
  }

  resetErrors() {
    this.isValid = true;
    this.validityError = '';
    this.validityElement = null;
    this.customError = '';
  }
}
